/**
 * Canonical URL normalization utilities (story 116-112)
 *
 * Provides deterministic normalization for request-derived canonical URLs:
 * - Strips known tracking query parameters (utm_*, fbclid, gclid)
 * - Applies the site's trailing-slash policy: strip trailing slashes from
 *   non-root paths; the root path "/" is always preserved as-is
 *
 * These helpers are applied when constructing canonical `<link>` and
 * `og:url` values derived from the live request URL so that tracking
 * parameters and slash variants never pollute the canonical signal.
 */

/** Tracking query-parameter prefixes / exact names to remove. */
const TRACKING_PARAM_PATTERNS: ReadonlyArray<RegExp> = [
  /^utm_/i,
  /^fbclid$/i,
  /^gclid$/i,
]

/**
 * Returns `true` when the supplied query-parameter name is a known
 * tracking parameter that should be stripped from canonical URLs.
 *
 * @param name - URL search-parameter name
 */
function isTrackingParam(name: string): boolean {
  return TRACKING_PARAM_PATTERNS.some((pattern) => pattern.test(name))
}

/**
 * Strips recognised tracking query parameters from a URL string.
 *
 * Only utm_*, fbclid, and gclid are removed; all other parameters are
 * preserved.  If no parameters remain after stripping, the trailing
 * `?` is also removed.
 *
 * @param url - Absolute or relative URL string
 * @returns URL string with tracking parameters removed
 *
 * @example
 * stripTrackingParams('https://example.com/page?utm_source=email&ref=nav')
 * // => 'https://example.com/page?ref=nav'
 *
 * stripTrackingParams('https://example.com/page?utm_source=email&utm_medium=cpc')
 * // => 'https://example.com/page'
 */
export function stripTrackingParams(url: string): string {
  if (!url || typeof url !== 'string') {
    return url
  }

  // For URLs without a query string, nothing to do.
  const queryIndex = url.indexOf('?')
  if (queryIndex === -1) {
    return url
  }

  const base = url.slice(0, queryIndex)
  const queryString = url.slice(queryIndex + 1)

  const kept: string[] = []
  for (const pair of queryString.split('&')) {
    if (!pair) continue
    const eqIndex = pair.indexOf('=')
    const name = eqIndex === -1 ? pair : pair.slice(0, eqIndex)
    if (!isTrackingParam(decodeURIComponent(name))) {
      kept.push(pair)
    }
  }

  return kept.length > 0 ? `${base}?${kept.join('&')}` : base
}

/**
 * Applies the site's trailing-slash policy to a URL string.
 *
 * Policy: the root path `/` retains its trailing slash; every other path
 * has its trailing slash removed.  Query strings and fragments are
 * preserved exactly.
 *
 * @param url - Absolute or relative URL string
 * @returns URL string with trailing-slash policy applied
 *
 * @example
 * applyTrailingSlashPolicy('https://example.com/about/')
 * // => 'https://example.com/about'
 *
 * applyTrailingSlashPolicy('https://example.com/')
 * // => 'https://example.com/'
 *
 * applyTrailingSlashPolicy('/blog/my-post/?ref=nav')
 * // => '/blog/my-post?ref=nav'
 */
export function applyTrailingSlashPolicy(url: string): string {
  if (!url || typeof url !== 'string') {
    return url
  }

  // Split off query string / fragment so we only touch the path segment.
  const fragmentIndex = url.indexOf('#')
  const queryIndex = url.indexOf('?')

  // Determine where the path portion ends.
  let suffixStart = -1
  if (queryIndex !== -1 && (fragmentIndex === -1 || queryIndex < fragmentIndex)) {
    suffixStart = queryIndex
  } else if (fragmentIndex !== -1) {
    suffixStart = fragmentIndex
  }

  const path = suffixStart === -1 ? url : url.slice(0, suffixStart)
  const suffix = suffixStart === -1 ? '' : url.slice(suffixStart)

  // Strip trailing slash unless the entire path segment is exactly '/'.
  // We also guard against protocol-relative or absolute URL origins like
  // 'https://example.com/' where the slash belongs to the origin.
  const stripped = path.endsWith('/')
    ? (() => {
        const withoutSlash = path.slice(0, -1)
        // Preserve root — an empty string after stripping means it was "/" alone.
        // Also preserve "https://example.com/" where stripping gives "https://example.com".
        if (withoutSlash === '' || withoutSlash.endsWith('://') || /^https?:\/\/[^/]+$/.test(withoutSlash)) {
          return path
        }
        return withoutSlash
      })()
    : path

  return `${stripped}${suffix}`
}

/**
 * Normalizes a request-derived canonical URL by:
 * 1. Stripping tracking query parameters (utm_*, fbclid, gclid)
 * 2. Applying the site's trailing-slash policy
 *
 * Use this on any URL obtained from `useRequestURL()` or the browser's
 * `location` before using it as a canonical value or og:url.
 *
 * @param url - Absolute or relative URL string derived from the request
 * @returns Normalized canonical URL string
 *
 * @example
 * normalizeCanonicalUrl('https://qx.qiksolve.com/about/?utm_source=email')
 * // => 'https://qx.qiksolve.com/about'
 *
 * normalizeCanonicalUrl('https://qx.qiksolve.com/')
 * // => 'https://qx.qiksolve.com/'
 *
 * normalizeCanonicalUrl('https://qx.qiksolve.com/blog/post?utm_medium=cpc&page=2')
 * // => 'https://qx.qiksolve.com/blog/post?page=2'
 */
export function normalizeCanonicalUrl(url: string): string {
  if (!url || typeof url !== 'string') {
    return url
  }

  return applyTrailingSlashPolicy(stripTrackingParams(url))
}
